/**
 * Utility to generate ultra high-resolution front and back card textures
 * for the 3D physics-based interactive Lanyard component.
 */

import { assets } from '../../assets/assets';

export function createLanyardTextures(): {
  front: Promise<string>;
  back: Promise<string>;
} {
  const width = 1200;
  const height = 1800;

  // Generate Front Texture
  const frontPromise = new Promise<string>((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      resolve('');
      return;
    }

    // Deep rich dark gradient background
    const bgGrad = ctx.createLinearGradient(0, 0, width, height);
    bgGrad.addColorStop(0, '#090D16');
    bgGrad.addColorStop(0.5, '#13112E');
    bgGrad.addColorStop(1, '#090D16');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, width, height);

    // Decorative geometric grid lines
    ctx.strokeStyle = 'rgba(139, 92, 246, 0.2)';
    ctx.lineWidth = 2;
    for (let x = 0; x < width; x += 80) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    for (let y = 0; y < height; y += 80) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Glowing background corner accents
    ctx.fillStyle = 'rgba(124, 58, 237, 0.45)';
    ctx.filter = 'blur(80px)';
    ctx.beginPath();
    ctx.arc(200, 200, 180, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(59, 130, 246, 0.35)';
    ctx.beginPath();
    ctx.arc(width - 200, height - 240, 200, 0, Math.PI * 2);
    ctx.fill();
    ctx.filter = 'none';

    // Top Header Banner
    const headerGrad = ctx.createLinearGradient(80, 100, width - 80, 100);
    headerGrad.addColorStop(0, '#A78BFA');
    headerGrad.addColorStop(0.5, '#C4B5FD');
    headerGrad.addColorStop(1, '#60A5FA');
    ctx.fillStyle = headerGrad;
    ctx.font = 'bold 48px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('NUGI.DEV', 90, 160);

    ctx.textAlign = 'right';
    ctx.font = 'bold 34px monospace';
    ctx.fillStyle = '#93C5FD';
    ctx.fillText('DEV PASS // 2026', width - 90, 160);

    // Header divider line
    ctx.strokeStyle = 'rgba(196, 181, 253, 0.5)';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(90, 200);
    ctx.lineTo(width - 90, 200);
    ctx.stroke();

    // Load profile photo
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Photo container glow ring
      const photoCenterX = width / 2;
      const photoCenterY = 560;
      const photoRadius = 230;

      ctx.save();
      // Outer glowing border
      ctx.strokeStyle = '#8B5CF6';
      ctx.lineWidth = 12;
      ctx.beginPath();
      ctx.arc(photoCenterX, photoCenterY, photoRadius + 8, 0, Math.PI * 2);
      ctx.stroke();

      // Clip image to circle
      ctx.beginPath();
      ctx.arc(photoCenterX, photoCenterY, photoRadius, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(
        img,
        photoCenterX - photoRadius,
        photoCenterY - photoRadius,
        photoRadius * 2,
        photoRadius * 2
      );
      ctx.restore();

      // Verified green badge
      ctx.fillStyle = '#10B981';
      ctx.beginPath();
      ctx.arc(photoCenterX + 165, photoCenterY + 165, 36, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#090D16';
      ctx.lineWidth = 8;
      ctx.stroke();

      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 36px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('✓', photoCenterX + 165, photoCenterY + 177);

      // Name (Huge, Bold, Ultra Clear)
      ctx.textAlign = 'center';
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 78px Inter, sans-serif';
      ctx.fillText('Nugitha Disas', width / 2, 910);

      // Title / Role (Large High-Contrast)
      const roleGrad = ctx.createLinearGradient(150, 970, width - 150, 970);
      roleGrad.addColorStop(0, '#DDD6FE');
      roleGrad.addColorStop(1, '#93C5FD');
      ctx.fillStyle = roleGrad;
      ctx.font = 'bold 44px Inter, sans-serif';
      ctx.fillText('ASPIRING FULL-STACK SOFTWARE ENGINEER', width / 2, 985);

      // Tech Stack Chips (Large, High-Contrast)
      const chips = ['Java', 'TypeScript' ,'Spring Boot', 'React', 'Angular'];
      ctx.font = 'bold 30px Inter, sans-serif,center';
      let startX = 110;
      const chipY = 1100;
      chips.forEach((chip) => {
        const textMetrics = ctx.measureText(chip);
        const cw = textMetrics.width + 44;
        ctx.fillStyle = 'rgba(124, 58, 237, 0.35)';
        ctx.strokeStyle = '#A78BFA';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.roundRect(startX, chipY, cw, 64, 20);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = '#FFFFFF';
        ctx.textAlign = 'left';
        ctx.fillText(chip, startX + 22, chipY + 44);
        startX += cw + 18;
      });

      // Bottom Barcode & ID Details
      ctx.strokeStyle = 'rgba(196, 181, 253, 0.4)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(90, 1340);
      ctx.lineTo(width - 90, 1340);
      ctx.stroke();

      // Simulated Barcode
      const bcY = 1400;
      const bcH = 80;
      ctx.fillStyle = '#C4B5FD';
      for (let bx = 120; bx < width - 120; bx += Math.random() > 0.4 ? 16 : 8) {
        ctx.fillRect(bx, bcY, Math.random() > 0.5 ? 6 : 4, bcH);
      }

      ctx.textAlign = 'center';
      ctx.fillStyle = '#E2E8F0';
      ctx.font = 'bold 30px monospace';
      ctx.fillText('AUTH-ID: 2909-ND-2026 // HOLD & DRAG TO SWING', width / 2, 1570);

      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = () => {
      // Fallback
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 78px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Nugitha Disas', width / 2, 900);
      resolve(canvas.toDataURL('image/png'));
    };

    img.src = assets.profile_picture;
  });

  // Generate Back Texture (Big, Bold, Crystal Clear)
  const backPromise = new Promise<string>((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      resolve('');
      return;
    }

    // Dark sleek gradient background
    const bgGrad = ctx.createLinearGradient(0, 0, width, height);
    bgGrad.addColorStop(0, '#090D16');
    bgGrad.addColorStop(0.5, '#13112E');
    bgGrad.addColorStop(1, '#090D16');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, width, height);

    // Decorative grid lines
    ctx.strokeStyle = 'rgba(59, 130, 246, 0.2)';
    ctx.lineWidth = 2;
    for (let x = 0; x < width; x += 80) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    for (let y = 0; y < height; y += 80) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Top Header
    ctx.fillStyle = '#C4B5FD';
    ctx.font = 'bold 46px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('ENGINEER CREDENTIALS', 90, 160);

    ctx.strokeStyle = 'rgba(129, 140, 248, 0.5)';
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(90, 200);
    ctx.lineTo(width - 90, 200);
    ctx.stroke();

    // Key Profile Stats / Info Cards with Large High-Contrast Text
    const infoItems = [
      { label: 'ACADEMIC DEGREE', value: 'Bachelor of Information Technology @ UCSC' },
      { label: 'CORE SPECIALTY', value: 'Full-Stack Architecture' },
      { label: 'LOCATION', value: 'Colombo, Sri Lanka' },
      { label: 'GITHUB', value: 'github.com/Nugi29' },
      { label: 'LINKEDIN', value: 'linkedin.com/in/nugithadisas' },
    ];

    let itemY = 270;
    infoItems.forEach((item) => {
      ctx.fillStyle = 'rgba(26, 21, 56, 0.85)';
      ctx.strokeStyle = 'rgba(167, 139, 250, 0.5)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.roundRect(90, itemY, width - 180, 170, 24);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#A78BFA';
      ctx.font = 'bold 26px Inter, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(item.label, 130, itemY + 60);

      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 38px Inter, sans-serif';
      ctx.fillText(item.value, 130, itemY + 120);

      itemY += 215;
    });

    // Holographic security badge at bottom
    const holoGrad = ctx.createLinearGradient(90, 1420, width - 90, 1600);
    holoGrad.addColorStop(0, 'rgba(124, 58, 237, 0.6)');
    holoGrad.addColorStop(0.5, 'rgba(56, 189, 248, 0.5)');
    holoGrad.addColorStop(1, 'rgba(124, 58, 237, 0.6)');

    ctx.fillStyle = holoGrad;
    ctx.strokeStyle = 'rgba(196, 181, 253, 0.7)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.roundRect(90, 1420, width - 180, 190, 30);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 36px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('⚡ VERIFIED SOFTWARE ENGINEER PASS', width / 2, 1500);

    ctx.fillStyle = '#E2E8F0';
    ctx.font = 'bold 26px Inter, sans-serif';
    ctx.fillText('Created by Nugi29 | A Software Engineer who loves building cool stuff', width / 2, 1555);

    resolve(canvas.toDataURL('image/png'));
  });

  return { front: frontPromise, back: backPromise };
}

